/**
 * ChatMsg — мемоизированный bubble одного сообщения в ленте чата.
 *
 * Главный смысл существования компонента: при поллинге messages list
 * каждые 5 сек react-query сохраняет ссылки на неизменившиеся объекты
 * (structural sharing). Если `m` пришёл тем же объектом → React.memo
 * пропускает re-render, и тяжёлые `<Avatar>`/`<VideoMessage>` внутри
 * не пересоздаются. Это убирает «дёрганье» при поллинге и при подгрузке
 * аватарок.
 *
 * Все callback-prop'ы должны передаваться обёрнутыми в useCallback в
 * родителе — иначе мемо не сработает (новые ссылки на каждый рендер).
 */
import { Fragment, memo, useMemo, useRef, useState } from "react";

import { Avatar } from "@/components/prototype/Avatar";
import { Icon } from "@/components/prototype/Icon";
import { VoiceMessage } from "@/components/VoiceMessage";
import { VideoMessage } from "@/components/VideoMessage";
import { downloadChatAttachment } from "@/lib/downloadAttachment";
import { VideoActionModal } from "@/components/VideoActionModal";
import { CollapsibleMarkdown } from "@/components/MiniMarkdown";
import { Emoji } from "@/components/Emoji";
import { chatAttachmentUrl, chatAttachmentPreviewUrl, type ChatMessage, type ChatAttachment } from "@/api/dashboard";
import { isOnline as isOnlineFromIso } from "@/lib/presence";


// Форматтеры — один раз на модуль: toLocale*String создаёт Intl.DateTimeFormat
// на каждый вызов, в CPU-профиле свайпов (16.09.2026) это было ~17% JS-времени.
// Кэш по iso + «сегодняшний» день (после полуночи «сегодня» меняется — ключ тоже).
const _fmtHM = new Intl.DateTimeFormat("ru-RU", { hour: "2-digit", minute: "2-digit" });
const _fmtDM = new Intl.DateTimeFormat("ru-RU", { day: "2-digit", month: "2-digit" });
const _fmtTimeCache = new Map<string, string>();
function fmtTime(iso: string): string {
  const today = new Date();
  const key = `${today.getFullYear()}-${today.getMonth()}-${today.getDate()}|${iso}`;
  const hit = _fmtTimeCache.get(key);
  if (hit !== undefined) return hit;
  const d = new Date(iso.endsWith("Z") ? iso : iso + "Z");
  if (Number.isNaN(d.getTime())) return "";
  const isToday = d.getFullYear() === today.getFullYear()
    && d.getMonth() === today.getMonth() && d.getDate() === today.getDate();
  const out = isToday ? _fmtHM.format(d) : `${_fmtDM.format(d)} ${_fmtHM.format(d)}`;
  if (_fmtTimeCache.size > 5000) _fmtTimeCache.clear();
  _fmtTimeCache.set(key, out);
  return out;
}

function fmtSize(bytes: number): string {
  if (bytes < 1024) return `${bytes} Б`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(0)} КБ`;
  return `${(bytes / 1024 / 1024).toFixed(1)} МБ`;
}

function isImage(mime: string | null | undefined, filename?: string | null): boolean {
  if (mime && mime.startsWith("image/")) return true;
  const f = (filename || "").toLowerCase();
  return /\.(png|jpe?g|gif|webp|bmp|avif|heic|heif|svg)$/.test(f);
}
function isAudio(mime: string | null | undefined, filename?: string | null): boolean {
  if (mime && mime.toLowerCase().startsWith("audio/")) return true;
  const f = (filename || "").toLowerCase();
  return /\.(mp3|wav|ogg|m4a|opus|aac)$/.test(f);
}
function isVideo(mime: string | null | undefined, filename?: string | null): boolean {
  const m = (mime || "").toLowerCase();
  if (m.startsWith("video/")) return true;
  if (m.startsWith("audio/")) return false;
  const f = (filename || "").toLowerCase();
  return /\.(mp4|mov|m4v|avi|mkv|webm)$/.test(f);
}


export type ChatMsgProps = {
  m: ChatMessage;
  /** Подпись разделителя дня перед этим сообщением (если день меняется). */
  dateSep: string | null;
  /** День этого сообщения для data-attribute (для sticky-date scroll-tracker'а). */
  msgDayLabel: string;
  mine: boolean;
  pending: boolean;
  deleted: boolean;
  editing: boolean;
  isJumpTarget: boolean;
  /** True если сообщение только что прилетело (для fade-in анимации). */
  isFresh?: boolean;
  avatarSize: number;
  /** True если long-press держит именно это сообщение. */
  pressed: boolean;
  /** Режим множественного выбора — клик по bubble переключает selected. */
  selectionMode: boolean;
  /** True если это сообщение выбрано в selection mode. */
  isSelected: boolean;
  /** Toggle selection для этого сообщения. */
  onToggleSelect: (id: number) => void;
  /** TG-style: ID отдельных аттачей которые выбраны (фото/видео в альбоме). */
  selectedAttachmentIds: Set<number>;
  /** Toggle для отдельного аттача. msgId + allAttIds нужны для explode-pattern:
   *  если whole-msg уже выделено, снятие чека с одного аттача разваливает
   *  выделение → msgId из selectedIds убирается, остальные аттачи добавляются. */
  onToggleAttachmentSelect: (msgId: number, attId: number, allAttIds: number[]) => void;
  /** Map для @-mentions: lower-cased slug → user_id. */
  mentionMap: Record<string, string>;
  /** Парсит текст с mentions/url/emoji в JSX. */
  renderText: (s: string) => React.ReactNode[];

  // Callbacks (должны быть useCallback'нуты в родителе)
  onProfileClick: (userId: string) => void;
  onLightboxOpen: (attachmentId: number) => void;
  onFilePreview: (att: ChatAttachment) => void;
  onContextMenu: (id: number, x: number, y: number, attId?: number) => void;
  onReply: (m: ChatMessage) => void;
  onReactToggle: (messageId: number, emoji: string) => void;
  onReplyJump: (id: number) => void;
  onTouchStart: (e: React.TouchEvent, messageId: number, blocked: boolean) => void;
  onTouchEnd: (e: React.TouchEvent) => void;
  onTouchMove: (e: React.TouchEvent) => void;
  /** Клик по inline-кнопке бота (m.meta.buttons[i]). Шлёт command как сообщение. */
  onBotButton?: (command: string, label: string) => void;
};


/** Длинные сообщения (>500 символов) сворачиваются с кнопкой «Показать больше».
 *  Render-функция получает полный текст — внутренние хэштеги/упоминания/ссылки
 *  рендерятся как обычно, визуально текст обрезан CSS-маской (.is-collapsed). */
/** Сообщение только из эмодзи (1–3 штуки) — рисуем крупно и без пузыря, как
 *  в Telegram. Считаем графемы (Intl.Segmenter), каждая должна быть эмодзи:
 *  пиктограмма или пара региональных флагов + модификаторы/ZWJ-последовательности.
 *  Возвращает число эмодзи или 0. */
const EMOJI_GRAPHEME_RE = /^(?:\p{Extended_Pictographic}|\p{Regional_Indicator}{2})(?:\uFE0F|\u20E3|\p{Emoji_Modifier}|\u200D(?:\p{Extended_Pictographic}|\p{Emoji_Modifier}|\uFE0F))*$/u;
function emojiOnlyCount(text: string | null | undefined): number {
  const t = (text || "").trim();
  if (!t || t.length > 40) return 0;
  const graphemes: string[] = [];
  const SegCtor = (Intl as unknown as { Segmenter?: new (l?: string, o?: { granularity: string }) => { segment: (s: string) => Iterable<{ segment: string }> } }).Segmenter;
  for (const part of t.split(/\s+/).filter(Boolean)) {
    if (SegCtor) {
      for (const g of new SegCtor(undefined, { granularity: "grapheme" }).segment(part)) graphemes.push(g.segment);
    } else {
      graphemes.push(part);
    }
    if (graphemes.length > 3) return 0;
  }
  if (graphemes.length === 0) return 0;
  return graphemes.every(g => EMOJI_GRAPHEME_RE.test(g)) ? graphemes.length : 0;
}

const LONG_TEXT_THRESHOLD = 500;
function CollapsibleText({ text, renderText }: {
  text: string;
  renderText: (s: string) => React.ReactNode[];
}) {
  const [expanded, setExpanded] = useState(false);
  const isLong = text.length > LONG_TEXT_THRESHOLD;
  // useMemo: renderText гонит regex'ы по тексту (mentions/hashtags/URL/emoji).
  // На длинных сообщениях бота (3-40 КБ) это сотни миллисекунд per render.
  // useCallback renderText в ChatPage стабилен, так что результат можно
  // кешировать по (text, renderText) и не парсить заново при каждом
  // re-render bubble'а (Virtuoso scroll → много lifecycle'ов).
  const rendered = useMemo(() => renderText(text), [text, renderText]);
  if (!isLong) {
    return <div className="chat-bubble-text">{rendered}</div>;
  }
  return (
    <>
      <div className={`chat-bubble-text chat-bubble-collapsible${expanded ? "" : " is-collapsed"}`}>
        {rendered}
      </div>
      <button
        type="button"
        className="chat-bubble-toggle"
        onClick={(e) => { e.stopPropagation(); setExpanded(v => !v); }}
      >
        {expanded ? "Свернуть" : "Показать больше"}
      </button>
    </>
  );
}


function ChatMsgInner(props: ChatMsgProps) {
  const {
    m, dateSep, msgDayLabel, mine, pending, deleted, editing, isJumpTarget, isFresh,
    avatarSize, pressed,
    selectionMode, isSelected, onToggleSelect, selectedAttachmentIds, onToggleAttachmentSelect,
    renderText,
    onProfileClick, onLightboxOpen, onFilePreview, onContextMenu, onReply,
    onReactToggle, onReplyJump, onTouchStart, onTouchEnd, onTouchMove,
    onBotButton,
  } = props;

  // Per-message double-click трекер (без межсобытийного состояния — каждый
  // bubble независимо считает свой 2× клик для reply).
  const lastClickRef = useRef<number | null>(null);
  // Видео: пикер папки для транскрибации/постмита + статус «запущено».
  const [videoAction, setVideoAction] = useState<{ attId: number; action: "transcribe" | "postmit" } | null>(null);
  const [videoStatus, setVideoStatus] = useState<string>("");
  // Только эмодзи (1–3) без ответа/вложений/пересылки → крупно и без пузыря (TG).
  const emojiOnly = useMemo(
    () => (!deleted && !editing && !m.reply_to && !(m.attachments && m.attachments.length) && !m.forwarded_from_message_id)
      ? emojiOnlyCount(m.text)
      : 0,
    [deleted, editing, m.reply_to, m.attachments, m.forwarded_from_message_id, m.text],
  );
  return (
    <Fragment>
      {dateSep && (
        <div className="chat-date-sep" data-date={dateSep}>
          <span>{dateSep}</span>
        </div>
      )}
      <div
        id={`chat-msg-${m.id}`}
        data-msg-date={msgDayLabel}
        className={`chat-msg${mine ? " is-mine" : ""}${pending ? " is-pending" : ""}${deleted ? " is-deleted" : ""}${isJumpTarget ? " is-jump-target" : ""}${selectionMode ? " is-selection-mode" : ""}${isSelected ? " is-selected" : ""}${isFresh ? " is-fresh" : ""}${emojiOnly ? ` is-emoji-only is-emoji-${emojiOnly}` : ""}`}
        onClick={selectionMode ? (e) => {
          e.stopPropagation();
          onToggleSelect(m.id);
        } : undefined}
      >
        {m.author_user_id ? (
          <button
            type="button"
            className="chat-avatar-btn"
            title="Открыть профиль"
            onClick={() => onProfileClick(m.author_user_id!)}
          >
            <Avatar
              name={m.author_name}
              userId={m.author_user_id}
              avatarFilename={m.author_avatar_filename}
              color={mine ? "#AB2B2B" : "#4A413B"}
              size={avatarSize}
              online={isOnlineFromIso(m.author_last_seen_at)}
              eager
            />
          </button>
        ) : (
          <Avatar
            name={m.author_name}
            color={mine ? "#AB2B2B" : "#4A413B"}
            size={avatarSize}
          />
        )}
        {/* Selection-mode radio — всегда в DOM, видимость через CSS-transition
            на родителе .chat-msg.is-selection-mode → плавный fade-in/fade-out
            при entering/exiting режима (юзер просил «отмена» без дёрганий). */}
        <span className={`chat-msg-radio${isSelected ? " is-checked" : ""}`} aria-hidden="true">
          {isSelected && <Icon name="check" size={14}/>}
        </span>
        <div className="chat-bubble-wrap" style={{ position: "relative" }}>
          {/* Hint иконка reply СПРАВА. ВСЕГДА в DOM (скрыта opacity:0) — родитель
              ведёт её opacity/scale императивно через ref во время свайпа, без
              per-frame setState. pointer-events:none чтобы не перехватывать тач. */}
          <div
            className="chat-swipe-reply-hint"
            style={{
              ...(mine ? { left: -36 } : { right: -36 }),
              opacity: 0,
              pointerEvents: "none",
            }}
          >
            <Icon name="reply" size={18}/>
          </div>
          <div
            className={`chat-bubble${pressed ? " is-pressed" : ""}`}
            style={pressed ? {
              transform: "scale(0.93)",
              transition: "transform 0.55s cubic-bezier(0.4, 0, 0.2, 1)",
            } : {
              transform: "translateX(0) scale(1)",
              transition: "transform 0.25s cubic-bezier(0.4, 0, 0.2, 1)",
            }}
            onContextMenu={(e) => {
              // deleted разрешаем — для удалённого меню покажет «Восстановить».
              if (pending || editing) return;
              // Если есть выделение — всё равно показываем наше меню
              // (там опция «Цитировать выделенное» дополнительно к копированию).
              e.preventDefault();
              e.stopPropagation();
              // Правый клик по видео (любой элемент с data-att-id) — меню
              // получит пункты для этого вложения («Скачать видео»).
              const attHost = (e.target as HTMLElement).closest("[data-att-id]") as HTMLElement | null;
              const attNum = attHost?.dataset.attId ? Number(attHost.dataset.attId) : NaN;
              onContextMenu(m.id, e.clientX, e.clientY, Number.isFinite(attNum) ? attNum : undefined);
            }}
            onClick={(e) => {
              if (pending || deleted || editing) return;
              const t = e.target as HTMLElement;
              if (t.closest("button, a, audio")) return;
              // Не перехватываем double-click если юзер выделяет/выделил текст —
              // browser auto-select word на dblclick важнее жеста reply.
              if (t.closest(".chat-bubble-text, .chat-bubble-reply-text, .chat-voice-text")) {
                return;
              }
              const now = Date.now();
              const last = lastClickRef.current;
              if (last && now - last < 350) {
                e.preventDefault();
                const sel = window.getSelection();
                if (sel) try { sel.removeAllRanges(); } catch { /* ignore */ }
                onReply(m);
                lastClickRef.current = null;
              } else {
                lastClickRef.current = now;
              }
            }}
            onTouchStart={(e) => onTouchStart(e, m.id, pending || editing)}
            onTouchEnd={onTouchEnd}
            onTouchMove={onTouchMove}
          >
            <div className="chat-bubble-meta">
              {m.author_user_id ? (
                <button
                  type="button"
                  className="chat-bubble-author chat-bubble-author-link"
                  title="Открыть профиль · ПКМ — действия · 2× клик — ответить"
                  onClick={() => onProfileClick(m.author_user_id!)}
                >
                  {m.author_name}
                </button>
              ) : (
                <strong className="chat-bubble-author">{m.author_name}</strong>
              )}
            </div>

            {m.forwarded_from_author_name && !deleted && (
              <div className="chat-bubble-forward">
                <Icon name="external" size={11}/>
                <span className="chat-bubble-forward-label">Переслано от</span>
                <span className="chat-bubble-forward-author">{m.forwarded_from_author_name}</span>
              </div>
            )}

            {m.reply_to && !deleted && (
              <button
                type="button"
                className="chat-bubble-reply"
                onClick={(e) => {
                  e.stopPropagation();
                  if (m.reply_to?.deleted) return;
                  onReplyJump(m.reply_to!.id);
                }}
              >
                <div className="chat-bubble-reply-body">
                  <div className="chat-bubble-reply-author">{m.reply_to.author_name}</div>
                  <div className="chat-bubble-reply-text">
                    {m.reply_to.deleted
                      ? <em>сообщение удалено</em>
                      : (m.reply_to.quote
                          ? <span className="chat-quoted">«{m.reply_to.quote}»</span>
                          : m.reply_to.preview)}
                  </div>
                </div>
              </button>
            )}

            {deleted ? (
              <div className="chat-bubble-text">сообщение удалено</div>
            ) : editing ? (
              <div className="chat-bubble-text" style={{ opacity: 0.55, fontStyle: "italic" }}>
                {m.text || "—"}
              </div>
            ) : (
              <>
                {m.text && <CollapsibleText text={m.text} renderText={renderText}/>}
                {/* Link preview карточка — генерится backend'ом в фоне после
                    send_message (см. _generate_link_preview_bg). Появляется
                    через message_edit SSE event. Клик — открывает оригинал. */}
                {m.meta?.link_preview && (() => {
                  const lp = m.meta.link_preview as { url: string; title?: string; description?: string; image_url?: string; site_name?: string };
                  return (
                    <a
                      href={lp.url}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="chat-bubble-linkpreview"
                      onClick={(e) => e.stopPropagation()}
                    >
                      {lp.image_url && (
                        <img
                          className="chat-bubble-linkpreview-img"
                          src={lp.image_url}
                          alt=""
                          loading="lazy"
                          decoding="async"
                        />
                      )}
                      <div className="chat-bubble-linkpreview-body">
                        {lp.site_name && <div className="chat-bubble-linkpreview-site">{lp.site_name}</div>}
                        {lp.title && <div className="chat-bubble-linkpreview-title">{lp.title}</div>}
                        {lp.description && <div className="chat-bubble-linkpreview-desc">{lp.description}</div>}
                      </div>
                    </a>
                  );
                })()}
                {(m.meta?.buttons ?? []).length > 0 && (
                  <div className="chat-bubble-buttons">
                    {(m.meta!.buttons!).map((btn, i) => (
                      <button
                        key={i}
                        type="button"
                        className="chat-bubble-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          onBotButton?.(btn.command, btn.label);
                        }}
                      >{btn.label}</button>
                    ))}
                  </div>
                )}
                {m.attachments && m.attachments.length > 0 && (() => {
                  const imgs = m.attachments.filter(a => isImage(a.mime, a.filename));
                  const audios = m.attachments.filter(a => isAudio(a.mime, a.filename));
                  const videos = m.attachments.filter(a => isVideo(a.mime, a.filename));
                  const files = m.attachments.filter(a =>
                    !isImage(a.mime, a.filename) &&
                    !isAudio(a.mime, a.filename) &&
                    !isVideo(a.mime, a.filename)
                  );
                  // Все ID аттачей этого сообщения — нужны для explode-pattern.
                  const allAttIds = m.attachments.map(a => a.id);
                  // Aspect-aware layouts (упрощённая версия TG grouped_layout):
                  // - 2 фото оба portrait → vertical stack (две portrait в строку
                  //   обрезаются по нижней трети, юзеры жалуются)
                  // - 5 фото → специальный layout: 2 large top + 3 small bottom
                  // - остальное — текущие квадратные grid'ы
                  const isPortrait = (a: ChatAttachment) =>
                    !!(a.width && a.height && a.height > a.width * 1.1);
                  const allPortrait = imgs.length >= 2 && imgs.every(isPortrait);
                  let albumClass: string;
                  if (imgs.length === 2) {
                    albumClass = allPortrait ? "is-2 is-stack" : "is-2";
                  } else if (imgs.length === 3) {
                    albumClass = "is-3";
                  } else if (imgs.length === 4) {
                    albumClass = "is-4";
                  } else if (imgs.length === 5) {
                    albumClass = "is-5";  // 2 top large + 3 bottom small
                  } else {
                    albumClass = "is-many";
                  }
                  // Для is-5 показываем все 5; иначе лимит 6.
                  const ALBUM_LIMIT = imgs.length === 5 ? 5 : 6;
                  const visible = imgs.slice(0, ALBUM_LIMIT);
                  const rest = imgs.length - visible.length;
                  return (
                    <div className="chat-bubble-attachments">
                      {imgs.length === 1 && (() => {
                        const a0 = imgs[0];
                        // Stripped thumbnail (TG-style): blurry placeholder
                        // на button (parent), под <img>. Пока src грузится —
                        // юзер видит цветовой каркас вместо серого прямоугольника.
                        // CSS .chat-bubble-img-btn.has-thumb рисует
                        // filter:blur(8px); как только img загрузился, он
                        // непрозрачно перекрывает фон.
                        const btnStyle = a0.thumb_b64
                          ? { backgroundImage: `url("data:image/jpeg;base64,${a0.thumb_b64}")`, backgroundSize: "cover", backgroundPosition: "center" }
                          : undefined;
                        // Visually checked: либо individually выделен, либо whole-msg выделено.
                        const attSelected = selectedAttachmentIds.has(a0.id) || isSelected;
                        return (
                          <button
                            type="button"
                            className={`chat-bubble-img-btn${attSelected ? " is-att-selected" : ""}${a0.thumb_b64 ? " has-thumb" : ""}`}
                            data-att-id={a0.id}
                            style={{
                              ...btnStyle,
                              // ФИКСИРОВАННАЯ высота 240×240 ВСЕГДА — независимо
                              // от dimensions картинки. 0 layout shift при load.
                              // Юзер откроет lightbox для просмотра real ratio.
                              width: 240,
                              height: 240,
                            }}
                            onContextMenu={(e) => {
                              if (pending || deleted || editing) return;
                              e.preventDefault();
                              e.stopPropagation();
                              onContextMenu(m.id, e.clientX, e.clientY, a0.id);
                            }}
                            onClick={(e) => {
                              e.stopPropagation();
                              if (selectionMode) {
                                onToggleAttachmentSelect(m.id, a0.id, allAttIds);
                              } else {
                                onLightboxOpen(a0.id);
                              }
                            }}
                          >
                            <img
                              className="chat-bubble-image"
                              src={chatAttachmentPreviewUrl(a0.id)}
                              sizes="240px"
                              alt={a0.filename}
                              decoding="async"
                              loading="lazy"
                              width={240}
                              height={240}
                            />
                          </button>
                        );
                      })()}
                      {imgs.length >= 2 && (
                        <div className={`chat-bubble-album ${albumClass}`}>
                          {visible.map((a, i) => {
                            const attSelected = selectedAttachmentIds.has(a.id) || isSelected;
                            return (
                              <button
                                key={a.id}
                                type="button"
                                className={`chat-bubble-album-cell${attSelected ? " is-att-selected" : ""}${a.thumb_b64 ? " has-thumb" : ""}`}
                                data-att-id={a.id}
                                style={a.thumb_b64 ? { backgroundImage: `url("data:image/jpeg;base64,${a.thumb_b64}")`, backgroundSize: "cover", backgroundPosition: "center" } : undefined}
                                onContextMenu={(e) => {
                                  if (pending || deleted || editing) return;
                                  e.preventDefault();
                                  e.stopPropagation();
                                  onContextMenu(m.id, e.clientX, e.clientY, a.id);
                                }}
                                onClick={(e) => {
                                  e.stopPropagation();
                                  if (selectionMode) {
                                    onToggleAttachmentSelect(m.id, a.id, allAttIds);
                                  } else {
                                    onLightboxOpen(a.id);
                                  }
                                }}
                              >
                                <img
                                  src={chatAttachmentPreviewUrl(a.id)}
                                  sizes="(max-width: 768px) 45vw, 240px"
                                  alt={a.filename}
                                  decoding="async"
                                  loading="lazy"
                                  width={a.width || 240}
                                  height={a.height || 240}
                                />
                                {i === visible.length - 1 && rest > 0 && (
                                  <span className="chat-bubble-album-more">+{rest}</span>
                                )}
                                {selectionMode && (
                                  <span className={`chat-att-radio${attSelected ? " is-checked" : ""}`}>
                                    {attSelected && <Icon name="check" size={12}/>}
                                  </span>
                                )}
                              </button>
                            );
                          })}
                        </div>
                      )}
                      {audios.map(a => (
                        <div key={a.id} className="chat-voice">
                          <VoiceMessage
                            src={chatAttachmentUrl(a.id)}
                            attachmentId={a.id}
                            durationSec={a.duration_sec}
                            waveform={a.waveform}
                            sizeBytes={a.size_bytes}
                            messageId={m.id}
                            authorName={m.author_name}
                            ts={m.created_at}
                          />
                          <div className={`chat-voice-text${a.transcription ? "" : " is-pending"}`}>
                            <span className="chat-voice-text-icon">
                              <Icon name={a.transcription ? "report" : "clock"} size={14}/>
                            </span>
                            {a.transcription || "распознавание…"}
                          </div>
                        </div>
                      ))}
                      {videos.map(a => {
                        // Результаты видео-задач живут в meta сообщения — рисуем их ПРЯМ ЗДЕСЬ,
                        // под видео, в одном бабле (владелец: «постмиты должны быть прям с видео,
                        // в одном сообщении»). Отдельных сообщений бота по ним больше нет.
                        const jobs = m.meta?.video_jobs?.[String(a.id)];
                        return (
                        <div key={a.id} className="chat-video">
                          <VideoMessage
                            id={a.id}
                            src={chatAttachmentUrl(a.id)}
                            filename={a.filename}
                            durationSec={a.duration_sec}
                            onOpen={() => onLightboxOpen(a.id)}
                          />
                          <div className="chat-video-meta">
                            <span>{a.filename}</span>
                            <span>· {fmtSize(a.size_bytes)}</span>
                          </div>
                          <div className="chat-video-actions">
                            <button type="button" className="chat-video-act"
                              onClick={() => { setVideoStatus(""); setVideoAction({ attId: a.id, action: "transcribe" }); }}>
                              <Icon name="report" size={14}/> Транскрибировать
                            </button>
                            <button type="button" className="chat-video-act"
                              onClick={() => { setVideoStatus(""); setVideoAction({ attId: a.id, action: "postmit" }); }}>
                              <Icon name="tag" size={14}/> Сделать постмит
                            </button>
                            <button type="button" className="chat-video-act"
                              title="Скачать видео на устройство"
                              onClick={(e) => { e.stopPropagation(); void downloadChatAttachment({ attachmentId: a.id, filename: a.filename, sizeBytes: a.size_bytes }); }}>
                              <Icon name="download" size={14}/> Скачать
                            </button>
                          </div>
                          {/* Транскрипт сам не показываем — это стена на 200+ реплик.
                              В чате только кнопка перехода к docx в Диске. */}
                          {jobs?.transcript && (
                            <button
                              type="button"
                              className="chat-video-drive-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                const t = jobs.transcript?.topic_id;
                                const f = jobs.transcript?.folder_id;
                                if (t) window.location.href = `?topic=${t}${f ? `&folder=${f}` : ""}`;
                              }}
                              disabled={!jobs.transcript.topic_id}
                              title={jobs.transcript.docx || "Транскрипт"}
                            >
                              <Icon name="report" size={14}/>
                              <span>Транскрипт в Диске</span>
                            </button>
                          )}
                          {jobs?.postmit && (
                            <div className="chat-video-result">
                              <div className="chat-video-result-head">
                                <Icon name="tag" size={13}/>
                                <span className="chat-video-result-title">Постмит</span>
                                {jobs.postmit.docx && (
                                  <span className="chat-video-result-file">
                                    {jobs.postmit.docx}{jobs.postmit.drive ? ` → «${jobs.postmit.drive}»` : ""}
                                  </span>
                                )}
                              </div>
                              {/* Постмит — markdown (# заголовки, таблицы, списки). renderText его не
        понимает и показал бы сырую разметку — поэтому свой рендерер. */}
    {jobs.postmit.text && <CollapsibleMarkdown text={jobs.postmit.text}/>}
                            </div>
                          )}
                        </div>
                        );
                      })}
                      {videoStatus && <div className="chat-video-status">{videoStatus}</div>}
                      {videoAction && (
                        <VideoActionModal
                          attachmentId={videoAction.attId}
                          action={videoAction.action}
                          onClose={() => setVideoAction(null)}
                          onStarted={(act) => setVideoStatus(act === "transcribe"
                            ? "🎬 Транскрибация запущена — результат придёт файлом в Диск и сообщением в чат."
                            : "📝 Постмит запущен — результат придёт docx в Диск и текстом в чат (Opus, ~30-60с).")}
                        />
                      )}
                      {files.map(a => (
                        <button
                          key={a.id}
                          type="button"
                          className="chat-attach-link chat-attach-link-with-preview"
                          onClick={(e) => { e.stopPropagation(); onFilePreview(a); }}
                          title="Открыть"
                        >
                          {/* Превью-thumbnail (Яндекс.Диск-стиль).
                              Backend выберет pipeline: PDF/Office → pdftoppm.
                              onError скрывает img → остаётся иконка. */}
                          <img
                            src={`/api/chat/attachments/${a.id}/preview-image`}
                            alt=""
                            loading="lazy"
                            decoding="async"
                            className="chat-attach-preview-thumb"
                            onError={(e) => {
                              const img = e.currentTarget;
                              img.style.display = "none";
                              const fb = img.nextElementSibling as HTMLElement | null;
                              if (fb) fb.style.display = "inline-flex";
                            }}
                          />
                          <span className="chat-attach-fallback-icon" style={{ display: "none" }}>
                            <Icon name="file" size={14}/>
                          </span>
                          <span className="chat-attach-link-name">{a.filename}</span>
                          <span className="chat-attach-link-size">{fmtSize(a.size_bytes)}</span>
                        </button>
                      ))}
                    </div>
                  );
                })()}
              </>
            )}

            {!deleted && m.reactions && m.reactions.length > 0 && (
              <div className="chat-reactions">
                {m.reactions.map(r => {
                  const users = r.users || [];
                  const visible = users.slice(0, 3);
                  const extra = r.count - visible.length;
                  return (
                    <button
                      key={r.emoji}
                      type="button"
                      className={`chat-reaction-chip${r.mine ? " is-mine" : ""}`}
                      onClick={() => onReactToggle(m.id, r.emoji)}
                      title={
                        users.length > 0
                          ? users.map((u) => u.display_name).join(", ") + (r.mine ? " (включая вас)" : "")
                          : (r.mine ? "Убрать реакцию" : "Поставить реакцию")
                      }
                    >
                      <span className="chat-reaction-emoji"><Emoji native={r.emoji}/></span>
                      {visible.length > 0 ? (
                        <>
                          <span className="chat-reaction-avatars">
                            {visible.map((u) => (
                              <Avatar
                                key={u.user_id}
                                name={u.display_name}
                                userId={u.user_id}
                                avatarFilename={u.avatar_filename}
                                size={18}
                                color="#AB2B2B"
                              />
                            ))}
                          </span>
                          {extra > 0 && (
                            <span className="chat-reaction-count">+{extra}</span>
                          )}
                        </>
                      ) : (
                        <span className="chat-reaction-count">{r.count}</span>
                      )}
                    </button>
                  );
                })}
              </div>
            )}

            {!editing && (
              <div className="chat-bubble-footer">
                {m.edited_at && !deleted && (
                  <span className="chat-bubble-footer-edit">изм.</span>
                )}
                <span className="chat-bubble-footer-time">
                  {pending ? "отправляется…" : fmtTime(m.created_at)}
                </span>
                {/* Раньше: && mine — показывали ✓N только на своих сообщениях.
                    Сейчас: индикатор «прочитали N» виден на ВСЕХ сообщениях. */}
                {!deleted && m.read_by && m.read_by.length > 0 && (
                  <span className="chat-bubble-footer-seen">
                    <svg width="16" height="11" viewBox="0 0 16 11" fill="none" aria-hidden="true">
                      <path d="M1 6.5L4 9.5L10 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                      <path d="M6 6.5L9 9.5L15 2.5" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round"/>
                    </svg>
                    {m.read_by.length}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </Fragment>
  );
}


export const ChatMsg = memo(ChatMsgInner);
